<template>
  <v-row dense>
    <v-col cols="6">
      <v-menu
        ref="menuFrom"
        v-model="menuFrom"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="290px"
        :readonly="readonly"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            :value="form.start | dateFormat"
            readonly
            role="button"
            autocomplete="nope"
            append-icon="mdi-calendar"
            :label="`${$t('SUBSCRIPTIONS.DATE_FROM')} *`"
            :error-messages="errors.start"
            :rules="getRulesByName('date_start')"
            v-on="on"
          />
        </template>
        <v-date-picker
          ref="picker"
          v-model="start"
          first-day-of-week="1"
          @change="saveDateFrom"
        />
      </v-menu>
    </v-col>
    <v-col cols="6">
      <v-menu
        ref="menuTo"
        v-model="menuTo"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="290px"
        :readonly="readonly"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            :value="form.end | dateFormat"
            readonly
            clearable
            role="button"
            autocomplete="nope"
            append-icon="mdi-calendar"
            :label="$t('SUBSCRIPTIONS.DATE_TO')"
            :error-messages="errors.end"
            :rules="getRulesByName('date_end')"
            @click:clear="clearDateTo"
            v-on="on"
          />
        </template>
        <v-date-picker
          ref="picker"
          v-model="end"
          :min="minDateTo"
          first-day-of-week="1"
          @change="saveDateTo"
        />
      </v-menu>
    </v-col>
    <v-col cols="12">
      <v-select
        v-model.number="form.billing_period"
        name="billing_period"
        item-text="name"
        item-value="id"
        :items="billingPeriodsList"
        :readonly="readonly"
        :label="`${$t('SUBSCRIPTIONS.BILLING_PERIOD')} *`"
        :rules="getRulesByName('billing_period')"
        :hint="hasPeriodStartsFrom"
        persistent-hint
        :error-messages="errors.billing_period"
        clearable
        @change="updateValue('billing_period', $event)"
      />
    </v-col>
    <v-col cols="6">
      <money-input
        v-model="form.price_per_month"
        name="price_per_month"
        :label="`${$t('CONTRACTS.PRICE_MONTH')} *`"
        :readonly="readonly"
        :error-messages="errors.price_per_month"
        :rules="getRulesByName('price_per_month')"
        @input="updateValue('price_per_month', $event)"
      />
    </v-col>
    <v-col cols="6">
      <money-input
        v-model="form.price_per_month_empty"
        name="price_per_month_empty"
        :label="`${$t('CONTRACTS.PRICE_MONTH_EMPTY')}`"
        :readonly="readonly"
        :error-messages="errors.price_per_month_empty"
        :rules="getRulesByName('price_per_month_empty')"
        @input="updateValue('price_per_month_empty', $event)"
      />
    </v-col>

    <v-col cols="6">
      <v-checkbox
        v-model="form.payment_in_advance"
        flat
        :label="`${$t('CONTRACTS.PAYMENT_IN_ADVANCE')}`"
        :error-messages="errors.payment_in_advance"
        messages=""
        @change="updateValue('payment_in_advance', $event)"
      />
    </v-col>
    <v-col cols="6">
      <v-select
        v-model.number="form.payable_within"
        name="payable_within"
        item-text="name"
        item-value="id"
        :items="payableWithinList"
        :readonly="readonly"
        :rules="getRulesByName('payable_within')"
        persistent-hint
        :error-messages="errors.payable_within"
        @change="updateValue('payable_within', $event)"
      />
    </v-col>

    <v-col cols="12">
      <div class="pa-3 theme--light grey lighten-4">
        <span class="pb-4 ">{{ $t('CONTRACTS.BOOKKEEPING_PARAM') }}</span>
        <v-row>
          <v-col cols="4">
            <v-text-field
              v-model.number="form.credit_account"
              name="credit_account"
              type="number"
              :label="`${$t('BOOKKEEPING.CREDIT_ACCOUNT')} *`"
              :readonly="readonly"
              :error-messages="errors.credit_account"
              :rules="getRulesByName('credit_account')"
              background-color="#fff"
              @input="updateValue('credit_account', $event)"
              @keypress="numbersOnly"
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model.number="form.business_division"
              name="business_division"
              type="number"
              :label="`${$t('BOOKKEEPING.BUSINESS_DIVISION')} *`"
              :readonly="readonly"
              :error-messages="errors.business_division"
              :rules="getRulesByName('business_division')"
              background-color="#fff"
              @input="updateValue('business_division', $event)"
              @keypress="numbersOnly"
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model.number="form.cost_center"
              name="cost_center"
              type="number"
              :label="`${$t('BOOKKEEPING.COST_CENTER')} *`"
              :readonly="readonly"
              :error-messages="errors.cost_center"
              :rules="getRulesByName('cost_center')"
              background-color="#fff"
              @input="updateValue('cost_center', $event)"
              @keypress="numbersOnly"
            />
          </v-col>
        </v-row>
      </div>
    </v-col>

    <v-col cols="12">
      <div class="pa-3 theme--light grey lighten-4">
        <span class="pb-4 ">{{
          $t('EREPORTER.INVOICES.BILLING_ADDRESS')
        }}</span>
        <v-row>
          <v-col cols="1">
            <v-checkbox
              v-model="form.use_billing_address"
              flat
              label=""
              :error-messages="errors.use_billing_address"
              messages=""
              @change="updateValue('use_billing_address', $event)"
            />
          </v-col>
          <v-col cols="7">
            <v-text-field
              v-model="form.per_address"
              :counter="100"
              :readonly="readonly"
              :rules="form.use_billing_address ? rules.notEmpty : []"
              :disabled="!form.use_billing_address"
              :label="`${$t('CONTRACTS.PER_ADDRESS')} *`"
              :error-messages="errors.per_address"
              @input="updateValue('per_address', $event)"
            />
          </v-col>
          <v-col cols="4">
            <address-form
              v-model="form"
              :errors="errors"
              :rules="
                form.use_billing_address
                  ? {
                      street: rules.notEmpty,
                      zip: rules.notEmpty,
                      city: rules.notEmpty
                    }
                  : {}
              "
              :disabled="!form.use_billing_address"
              :readonly="readonly"
              @update-prop="updateValue"
            />
          </v-col>
        </v-row>
      </div>
    </v-col>

    <v-col cols="12"
      ><v-checkbox
        v-model="form.without_vat"
        name="without_vat"
        :readonly="readonly"
        :label="$t('CONTRACTS.VAT_INCL')"
        :error-messages="errors.without_vat"
        @change="updateValue('without_vat', $event, 'person')"
    /></v-col>
    <v-col cols="4">
      <v-checkbox
        v-model="form.specify_buildings"
        name="without_vat"
        :readonly="readonly"
        :error-messages="errors.specify_buildings"
        @change="updateValue('without_vat', $event, 'person')"
      >
        <template v-slot:label>
          <span class="pr-2">{{ $t('CONTRACTS.SPECS_BUILDINGS') }}</span>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">
                mdi-information-variant-circle-outline
              </v-icon>
            </template>
            <span> {{ $t('CONTRACTS.INFO_MESSAGE') }}</span>
          </v-tooltip>
        </template>
      </v-checkbox>
    </v-col>
    <v-col v-if="form.specify_buildings" cols="12">
      <buildings-input
        v-model="form.building_ids"
        :building-complex-id="buildingComplexId"
        :rules="getRulesByName('building_ids')"
        :error-messages="errors.building_id"
        :label="`${$t('BUILDINGS.ITEMS')} *`"
        @change="updateValue('building_ids', $event)"
      />
    </v-col>
  </v-row>
</template>

<script>
import { dateFormat, dateFormatBackend } from '@/modules/_global/filters';
import { numbersOnly } from '@/modules/_global/utils/events';
import {
  requiredSelectNotArray,
  textDescription80,
  requiredSelectArray,
  isNumeric,
  notEmpty
} from '@/modules/_global/variables/rules.js';
import formEditable from '@/modules/_global/mixins/form-editable';
import { mapCacheActions } from 'vuex-cache';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import BuildingsInput from '@/modules/building_complexes_contracts/components/forms/BuildingsInput';
import AddressForm from '@/modules/addresses/components/forms/AddressForm.vue';
import MoneyInput from '@/modules/_global/_components/money/MoneyInput.vue';

export default {
  name: 'ContractForm',

  components: { MoneyInput, AddressForm, BuildingsInput },

  filters: {
    dateFormat,
    dateFormatBackend
  },

  mixins: [formEditable, i18nRefreshData],

  props: {
    buildingComplexId: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    form: {
      without_vat: false,
      specify_buildings: false,
      building_ids: [],
      start: '',
      end: '',
      price_per_month_empty: null,
      price_per_month: null,
      credit_account: null,
      business_division: null,
      cost_center: null,
      use_billing_address: true,
      zip: null,
      street: null,
      city: null,
      per_address: null,
      payment_in_advance: false,
      payable_within: 30
    },
    rules: {
      related_id: requiredSelectNotArray,
      priority: requiredSelectNotArray,
      building_ids: requiredSelectArray,
      notes: textDescription80,
      business_division: [isNumeric, notEmpty],
      cost_center: [isNumeric, notEmpty],
      notEmpty: [notEmpty],
      credit_account: [isNumeric, notEmpty]
    },
    menuFrom: false,
    menuTo: false,
    buildingsList: [],
    billingPeriodsList: [],
    payableWithinList: []
  }),

  computed: {
    hasPeriodStartsFrom() {
      if (!this.isCreate && this.form.start_billing) {
        return `${this.$t('CONTRACTS.PERIOD_STARTS_HINT')} ${dateFormat(
          this.form.start_billing
        )}`;
      }
      return '';
    },
    end: {
      get() {
        return dateFormatBackend(this.form.end);
      },
      set(v) {
        this.form.end = v;
      }
    },

    start: {
      get() {
        return dateFormatBackend(this.form.start);
      },
      set(v) {
        this.form.start = v;
      }
    },

    minDateTo() {
      return dateFormatBackend(this.form.start);
    }
  },

  created() {
    this.refreshData();
  },

  methods: {
    numbersOnly,
    ...mapCacheActions('contracts', ['getBillingPeriods', 'getPayableWithin']),

    getDataBillingPeriods() {
      return this.getBillingPeriods().then(({ data }) => {
        this.billingPeriodsList = [...data.data];

        return data;
      });
    },

    getDataPayableWithin() {
      return this.getPayableWithin().then(({ data }) => {
        this.payableWithinList = [...data.data];

        return data;
      });
    },

    clearDateTo() {
      this.form.end = null;
    },

    saveDateTo(date) {
      this.$refs.menuTo.save(date);
      this.updateValue('end', date);
    },

    saveDateFrom(date) {
      this.$refs.menuFrom.save(date);
      this.updateValue('start', date);
    },

    refreshData() {
      this.getDataBillingPeriods();
      this.getDataPayableWithin();
    }
  }
};
</script>

<style scoped></style>
